import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, ArrowLeft, Building2 } from "lucide-react";
import { getStateCities } from "@/lib/api";
import { T } from "@/context/LanguageContext";
import { Seo } from "@/components/site/Seo";
import { itemListJsonLd, breadcrumbJsonLd } from "@/lib/seo";
import { SectionHeading } from "@/components/home/ExploreByState";

export default function StateCities() {
  const { stateSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getStateCities(stateSlug)
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [stateSlug]);

  const list = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    if (!q) return data.cities;
    return data.cities.filter((c) => c.name.toLowerCase().includes(q));
  }, [data, query]);

  if (loading) {
    return (
      <div className="py-24 text-center text-muted-foreground" data-testid="state-cities-loading">
        <T>Loading cities & towns…</T>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center" data-testid="state-cities-notfound">
        <p className="text-lg text-muted-foreground"><T>We couldn't find that state.</T></p>
        <Link to="/explore/states" className="mt-4 inline-flex items-center gap-1.5 font-semibold text-oldglory-red">
          <ArrowLeft className="h-4 w-4" /> <T>Back to all states</T>
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1500px] px-4 py-12" data-testid="state-cities-page">
      <Seo
        title={`Cities & Towns in ${data.state.name}`}
        description={`Browse all ${data.count} cities, towns and communities in ${data.state.name} on USAPulse — ordered by population, each with its own local portal of people, history, economy and daily pulse.`}
        path={`/explore/${stateSlug}/cities`}
        jsonLd={[
          itemListJsonLd(`Cities & Towns in ${data.state.name}`, data.cities),
          breadcrumbJsonLd([
            { name: "USAPulse", url_path: "/" },
            { name: data.state.name, url_path: `/place${data.state.url_path}` },
            { name: "Cities & Towns", url_path: `/explore/${stateSlug}/cities` },
          ]),
        ]}
      />
      <Link
        to={`/place${data.state.url_path}`}
        className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-navy/70 hover:text-navy"
        data-testid="state-cities-back"
      >
        <ArrowLeft className="h-4 w-4" /> <T>Back to</T> {data.state.name}
      </Link>

      <SectionHeading kicker="Every city & town" title={`Cities & Towns in ${data.state.name}`} />

      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-2xl text-sm text-muted-foreground">
          <T>Browse every city, town and community we track across</T> {data.state.name}
          <T>, ordered by population. Open any place for its living local portal.</T>
        </p>
        <div className="shrink-0 rounded-xl bg-navy px-4 py-2 text-center text-white" data-testid="state-cities-count">
          <div className="font-display text-2xl font-bold leading-none">{data.count}</div>
          <div className="text-[10px] uppercase tracking-wider text-white/70">Places</div>
        </div>
      </div>

      <div className="relative mb-8 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Filter cities in ${data.state.name}…`}
          className="w-full rounded-full border border-border bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-navy focus:ring-2 focus:ring-navy/20"
          data-testid="state-cities-search"
        />
      </div>

      {list.length === 0 ? (
        <div className="py-24 text-center text-muted-foreground" data-testid="state-cities-empty">
          <T>No cities match your filter.</T>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {list.map((c, i) => (
            <motion.div
              key={c.geo_id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i, 24) * 0.02, duration: 0.3 }}
            >
              <Link
                to={`/place${c.url_path}`}
                className="group flex h-full flex-col justify-between rounded-xl border border-border bg-white p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:border-navy/30 hover:shadow-xl"
                data-testid={`state-city-card-${c.geo_id}`}
              >
                <div className="flex items-start gap-2">
                  <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-oldglory-red/70" />
                  <div className="font-display text-base font-bold leading-tight text-navy">{c.name}</div>
                </div>
                <div className="mt-3 flex items-center justify-between border-t border-border pt-2 text-[11px] text-muted-foreground">
                  <span className="flex items-center gap-1 capitalize">
                    <MapPin className="h-3 w-3" /> {c.geo_type}
                  </span>
                  <span>{c.population ? c.population.toLocaleString() : "—"}</span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
